import Axios from "axios";
import { UserTracker } from "components/user-tracker";
import { LastAppointmentProvider } from "context/last-appointment";
import { Loader } from "react-feather";
import { get } from "lodash";
import AvailabilityPage from "pages/availability";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { IAnalyticsEventTracker, IAnalyticsService } from "utils/analytics";
import ENDPOINTS from "utils/endpoints";
import styles from "./App.module.css";
import AppContext from "./context/app";
import User, {
    mostRecentCompletedAppointmentForTracking,
    upcomingAppointmentForTracking,
} from "./models/user";
import EmailConfirmationPage from "./pages/email-confirmation";
import HomePage from "./pages/home";
import LoginPage from "./pages/login";
import MaintenanceModePage from "./pages/maintenance-mode";
import PasswordForgottenPage from "./pages/password-forgotten";
import PasswordResetPage from "./pages/password-reset";
import ProfilePage from "./pages/profile";
import SignUpPage from "./pages/sign-up";
import SharePage from "./pages/share";
import TfaInputPage from "./pages/tfa-input";
import TfaSettingsPage from "./pages/tfa-settings";
import UnsubscribePage from "./pages/unsubscribe";
// Pages
import WidgetPage from "./pages/widgets";
import { getTranslations } from "./translations";
import { createAnalyticsService } from "./utils/analytics/serviceFactory";
import { IFeatureManager } from "./utils/feature-management";
import {
    AmplitudeFlagAndExperimentManager,
    FeatureFlag,
} from "./utils/feature-management/amplitude";
import {
    getPersistedAuthToken,
    getPersistedUserLanguage,
    persistAuthToken,
    persistUserLanguage,
} from "./utils/local-storage";
import ROUTES from "./utils/routes";
import { UserLanguage } from "./utils/types";

const App: React.FunctionComponent = () => {
    const location = useLocation();
    // States and refs

    const [authToken, setAuthToken] = useState<string | null>(
        getPersistedAuthToken()
    );
    const [authUser, setAuthUser] = useState<User | null>(null);
    const [language, setLanguage] = useState<UserLanguage>(
        getPersistedUserLanguage()
    );
    const [analytics, setAnalytics] = useState<IAnalyticsService | null>(null);
    const [
        featureManager,
        setFeatureManager,
    ] = useState<IFeatureManager | null>(null);
    const [hasResolvedGlobalFlags, setHasResolvedGlobalFlags] = useState(false);
    const [
        currentFeatureFlagUserEmail,
        setLoadedFeatureFlagUserEmail,
    ] = useState<string | null>(null);
    const [, setFeatureManagerStateVersion] = useState(0);

    const isMaintenanceModeOn = featureManager?.isFeatureFlagEnabled(
        "maintenance-mode",
        FeatureFlag.On
    );

    useEffect(() => {
        if (!featureManager) {
            setFeatureManagerStateVersion(0);
            return;
        }

        setFeatureManagerStateVersion(featureManager.getStateVersion());

        return featureManager.subscribe(() => {
            setFeatureManagerStateVersion(featureManager.getStateVersion());
        });
    }, [featureManager]);

    // Effects
    useEffect(() => {
        let analyticsService: IAnalyticsService;

        if (!analytics) {
            // NOTE: we force String conversion of the env variable to ensure that the appropriate
            // Javascript type String is assigned to the environment value during build and deployment
            const cdpConfig = {
                writeKey: process.env.REACT_APP_CUSTOMERIO_CDP_WRITE_KEY ?? "",
                cdnURL: process.env.REACT_APP_CUSTOMERIO_CDP_CDN_URL ?? "",
                isProxyCdnURL:
                    String(
                        process.env.REACT_APP_CUSTOMERIO_CDP_IS_PROXY_CDN_URL
                    ) === "true",
            };
            const webConfig = {
                apiHostURL: process.env.REACT_APP_PLAUSIBLE_HOST_URL ?? "",
                domainURL: process.env.REACT_APP_PLAUSIBLE_DOMAIN_URL ?? "",
                trackLocalhost:
                    String(process.env.REACT_APP_PLAUSIBLE_TRACK_LOCALHOST) ===
                    "true",
            };

            analyticsService = createAnalyticsService({
                cdp: cdpConfig,
                web: webConfig,
            });

            setAnalytics(analyticsService);
        }

        if (!featureManager) {
            // NOTE: This check allows us to run experiments in any environment which the API KEY
            // is defined. If key is not defined, app continues execution instead of throwing errors
            if (
                process.env.REACT_APP_AMPLITUDE_EXPERIMENT_API_KEY &&
                process.env.REACT_APP_AMPLITUDE_EXPERIMENT_SERVER_URL
            ) {
                const startAmplitudeFlagAndExperimentManager = async (): Promise<void> => {
                    const eventTracker =
                        (analyticsService.cdp() as IAnalyticsEventTracker) ??
                        null;
                    const manager = new AmplitudeFlagAndExperimentManager(
                        process.env.REACT_APP_AMPLITUDE_EXPERIMENT_API_KEY ??
                            "",
                        process.env
                            .REACT_APP_AMPLITUDE_EXPERIMENT_SERVER_URL as string,
                        String(
                            process.env.REACT_APP_AMPLITUDE_EXPERIMENT_DEBUG
                        ) === "true",
                        eventTracker
                    );

                    if (manager) {
                        const canStart = await manager.start();
                        if (canStart) {
                            setFeatureManager(manager);
                            try {
                                await manager.fetchGlobalFlags();
                            } catch (error) {
                                console.error(error);
                                manager.setFallbackFlagsEnabled(true);
                            }
                        }

                        setHasResolvedGlobalFlags(true);
                    }
                };

                startAmplitudeFlagAndExperimentManager();
            } else {
                console.warn("Feature Manager is not configured");
                setHasResolvedGlobalFlags(true);
            }
        }
    }, []);

    useEffect(() => {
        analytics?.web()?.pageView();
    }, [location, analytics]);

    useLayoutEffect(() => {
        // Persist the token
        persistAuthToken(authToken);

        // If no auth token, no auth user
        if (!authToken) setAuthUser(null);

        // Update axios configs to change the used auth token
        const requestInterceptor = Axios.interceptors.request.use(
            (config) => {
                config.headers["accept-language"] = language;
                config.headers["x-access-token"] = authToken;
                return config;
            },
            (error) => Promise.reject(error)
        );

        return () => {
            Axios.interceptors.request.eject(requestInterceptor);
        };
    }, [authToken, setAuthUser, language]);

    useEffect(() => {
        // Fetch auth user
        if (authToken !== null && authToken !== undefined) {
            fetchMe();
        }
    }, [authToken]);

    useEffect(() => {
        if (authUser === null || analytics === null) {
            return;
        }

        const nextAppointment = upcomingAppointmentForTracking(authUser);
        const lastAppointment = mostRecentCompletedAppointmentForTracking(
            authUser
        );

        analytics
            .cdp()
            ?.identifyUser(
                authUser.firstName ?? "",
                authUser.email ?? "",
                authUser.phone ?? "",
                authUser.lang ?? "",
                nextAppointment?.datetime ?? "",
                lastAppointment?.datetime ?? "",
                authUser.created_at ?? ""
            );
    }, [authUser, analytics]);

    useEffect(() => {
        if (!featureManager || !hasResolvedGlobalFlags || isMaintenanceModeOn) {
            return;
        }

        const featureFlagUserEmail = authUser?.email ?? null;
        if (currentFeatureFlagUserEmail === featureFlagUserEmail) {
            return;
        }

        const fetchFeatureFlags = async (): Promise<void> => {
            try {
                if (featureFlagUserEmail) {
                    await featureManager.fetchUserFlags(featureFlagUserEmail);
                } else {
                    await featureManager.fetchGlobalFlags();
                }

                setLoadedFeatureFlagUserEmail(featureFlagUserEmail);
            } catch (error) {
                console.error(error);
                featureManager.setFallbackFlagsEnabled(true);
                setLoadedFeatureFlagUserEmail(featureFlagUserEmail);
            }
        };

        fetchFeatureFlags();
    }, [
        authUser?.email,
        featureManager,
        hasResolvedGlobalFlags,
        isMaintenanceModeOn,
        currentFeatureFlagUserEmail,
    ]);

    useEffect(() => {
        const responseInterceptor = Axios.interceptors.response.use(
            (res) => res,
            (error) => {
                const errorStatus = get(
                    error,
                    ["response", "data", "status"],
                    400
                );
                if (errorStatus === 401 || errorStatus === 403)
                    setAuthToken(null);
                return Promise.reject(error);
            }
        );

        return () => {
            Axios.interceptors.response.eject(responseInterceptor);
        };
    }, [setAuthToken]);

    useEffect(() => {
        persistUserLanguage(language);
        if (authUser && authUser.lang !== language) updateUserLanguage();
    }, [language, authUser]);

    // Network

    const fetchMe = async () => {
        try {
            const { data } = await Axios.get(ENDPOINTS.ME);
            setAuthUser(data);
        } catch (e) {
            setAuthToken(null);
        }
    };

    const updateUserLanguage = async () => {
        try {
            await Axios.put(ENDPOINTS.ME, { lang: language });
            if (authUser) setAuthUser({ ...authUser, lang: language });
        } catch (e) {
            console.log(e);
        }
    };

    // Rendering

    const translations = getTranslations(language);

    const loadingIndicator = (
        <div className={styles.loaderContainer}>
            <div className={styles.loader}>
                <Loader className="spinorama" />
                <p>{translations.common.loading}</p>
            </div>
        </div>
    );
    const hasLoadedUserFeatureFlags =
        !authToken ||
        (!!authUser?.email && currentFeatureFlagUserEmail === authUser.email);

    return (
        <AppContext.Provider
            value={{
                authToken,
                setAuthToken,
                authUser,
                setAuthUser,
                language,
                setLanguage,
                translations,
                analytics,
                setAnalytics,
                featureManager,
                setFeatureManager,
            }}
        >
            <LastAppointmentProvider>
                {!hasResolvedGlobalFlags ? (
                    loadingIndicator
                ) : isMaintenanceModeOn ? (
                    <MaintenanceModePage />
                ) : (
                    <Switch>
                        <Route exact path={ROUTES.AVAILABILITY}>
                            <AvailabilityPage />
                        </Route>
                        <Route exact path="/widgets">
                            <WidgetPage />
                        </Route>
                        <Route exact path={ROUTES.LOGIN}>
                            <UserTracker>
                                <LoginPage />
                            </UserTracker>
                        </Route>
                        <Route exact path={ROUTES.TFA_INPUT}>
                            <TfaInputPage />
                        </Route>
                        <Route exact path={ROUTES.TFA_SETTINGS}>
                            <TfaSettingsPage />
                        </Route>
                        <Route exact path={ROUTES.EMAIL_CONFIRMATION}>
                            <EmailConfirmationPage />
                        </Route>
                        <Route exact path={ROUTES.PASSWORD_FORGOTTEN}>
                            <PasswordForgottenPage />
                        </Route>
                        <Route exact path={ROUTES.PASSWORD_RESET}>
                            <PasswordResetPage />
                        </Route>
                        <Route exact path={ROUTES.SIGN_UP}>
                            <UserTracker>
                                <SignUpPage />
                            </UserTracker>
                        </Route>
                        <Route exact path={ROUTES.UNSUBSCRIBE}>
                            <UnsubscribePage />
                        </Route>
                        <Route exact path={ROUTES.SHARE}>
                            <SharePage />
                        </Route>

                        {!authToken && <Redirect to={ROUTES.LOGIN} />}
                        <Route path={ROUTES.HOME}>
                            {hasLoadedUserFeatureFlags ? (
                                <HomePage />
                            ) : (
                                loadingIndicator
                            )}
                        </Route>
                        <Route exact path={ROUTES.PROFILE}>
                            <ProfilePage />
                        </Route>
                        <Redirect to={ROUTES.HOME} />
                    </Switch>
                )}
            </LastAppointmentProvider>
        </AppContext.Provider>
    );
};

export default App;
